import { type ReactElement } from 'react';
import type { ValidationRule } from '@/api/v2Client';
import { ruleRowCellStyle as tdStyle } from './styles';
import { EnforcementBadge } from './EnforcementBadge';

// Phase 5.X-validation-rules-tier-grouping —
// Extracted from ValidationRulesPage.tsx so the same row template can
// be reused across multiple per-tier <table>s rather than being
// repeated inline. Behavior is identical to the pre-refactor inline
// <tr>; only the wrapping changed.

const SEVERITY_COLORS: Record<string, string> = {
  error: 'var(--red)',
  Error: 'var(--red)',
  warning: 'var(--amber)',
  Warning: 'var(--amber)',
  info: 'var(--accent)',
  Info: 'var(--accent)',
};

function severityColor(s: string): string {
  return SEVERITY_COLORS[s] ?? 'var(--text-muted)';
}

// phase-fix-validation-rules-region-column — the populated, authoritative
// per-rule agency field is `Agencies` (V1 parity: V1's screen rendered
// r.agencies); the `Region` column in the DB was never populated. 'All'
// displays as Global; a blank value renders a VISIBLE 'Unknown', never a
// silent blank (project convention).
function agenciesLabel(agencies: string | null | undefined): string {
  const v = (agencies ?? '').trim();
  if (v === '') return 'Unknown';
  if (v === 'All') return 'Global (All)';
  return v;
}

export interface RuleRowProps {
  readonly rule: ValidationRule;
}

// phase-validation-rules-screen — the agency-vs-internal distinction, explicit.
// 'agency' = a regulator/ICH mandates this (breaking it breaks a REGULATOR'S
// requirement); 'internal' = AutoeCTD's own quality check. Very different
// consequences — the badge makes them unmistakable at a glance.
function OriginBadge({ origin }: { origin: string | undefined }): ReactElement {
  const isInternal = origin === 'internal';
  return (
    <span
      data-origin={isInternal ? 'internal' : 'agency'}
      title={
        isInternal
          ? 'AutoeCTD’s own quality check — not a regulator requirement'
          : 'Mandated by a regulator / ICH specification'
      }
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.03em',
        whiteSpace: 'nowrap',
        background: isInternal ? '#e0e7ff' : '#fee2e2',
        color: isInternal ? '#3730a3' : '#7f1d1d',
        border: `1px solid ${isInternal ? '#c7d2fe' : '#fca5a5'}`,
      }}
    >
      {isInternal ? 'AutoeCTD check' : 'Agency requirement'}
    </span>
  );
}

export function RuleRow({ rule }: RuleRowProps): ReactElement {
  return (
    <tr
      style={{ borderBottom: '1px solid var(--border)' }}
      data-testid={`rule-row-${rule.RuleId}`}
    >
      <td
        style={{
          ...tdStyle,
          fontFamily: 'monospace',
          fontSize: 12,
          whiteSpace: 'nowrap',
        }}
      >
        {rule.RuleId}
        <div style={{ marginTop: 4 }} data-testid={`rule-origin-${rule.RuleId}`}>
          <OriginBadge origin={rule.Origin} />
        </div>
      </td>
      <td style={tdStyle}>
        {rule.Description}
        {/* Provenance — the audit answer to "on what authority does this rule
            exist?" An absent source renders an explicit Unattributed marker,
            never a silent blank. */}
        <div
          data-testid={`rule-provenance-${rule.RuleId}`}
          style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}
        >
          {(rule.Reference ?? '').trim() !== '' ? (
            <>Source: {rule.Reference}</>
          ) : (
            <em>Unattributed — source not established</em>
          )}
        </div>
        {(rule.Notes ?? '').trim() !== '' && (
          <div data-testid={`rule-notes-${rule.RuleId}`} style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
            Notes: {rule.Notes}
          </div>
        )}
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} data-testid={`rule-agencies-${rule.RuleId}`}>
        {agenciesLabel(rule.Agencies)}
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
        <span
          style={{
            background: severityColor(rule.Severity),
            color: '#fff',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {rule.Severity}
        </span>
        <div style={{ marginTop: 4 }} data-testid={`rule-enforcement-${rule.RuleId}`}>
          <EnforcementBadge enforcement={rule.Enforcement} />
        </div>
      </td>
      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{rule.Category ?? '—'}</td>
    </tr>
  );
}
